import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faCheck } from '@fortawesome/free-solid-svg-icons';
import './combo-input.scss';

// A text field that also offers a preset list: type anything, or open the chevron and
// pick. Replaces <input list> + <datalist>, whose dropdown is drawn by the OS and
// cannot be styled to match the app.
//
// The list renders in a portal because these fields sit inside scrolling panels that
// would otherwise clip it.
const ComboInput = ({
    value = '',
    options = [],
    placeholder = '',
    emptyText = 'No matches',
    onValueChange,
    onCommit,
    onDoubleClick,
}) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [box, setBox] = useState(null);
    // What the user has typed since opening. null means they haven't typed, so the
    // whole list shows -- the field's existing value is a value, not a search query.
    const [query, setQuery] = useState(null);

    const wrapRef = useRef(null);
    const inputRef = useRef(null);
    const listRef = useRef(null);
    // What the caller last persisted, so picking an option and then blurring doesn't
    // fire the same save twice.
    const committedRef = useRef(value);

    // Typing narrows the presets; opening the chevron always shows every one. A value
    // of your own still shows the full list rather than an empty dropdown.
    const filtered = useMemo(() => {
        const needle = String(query ?? '').trim().toLowerCase();
        if (!needle) return options;
        const matches = options.filter((option) => String(option).toLowerCase().includes(needle));
        return matches.length ? matches : options;
    }, [query, options]);

    const place = () => {
        const el = wrapRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const below = window.innerHeight - rect.bottom;
        const wantsFlip = below < 220 && rect.top > below;
        setBox({
            left: rect.left,
            width: Math.max(rect.width, 148),
            top: wantsFlip ? null : rect.bottom + 4,
            bottom: wantsFlip ? window.innerHeight - rect.top + 4 : null,
            maxHeight: Math.max(120, (wantsFlip ? rect.top : below) - 12),
        });
    };

    useLayoutEffect(() => {
        if (!open) return;
        place();
        // Any ancestor can scroll, so listen in the capture phase.
        const onScroll = () => place();
        window.addEventListener('scroll', onScroll, true);
        window.addEventListener('resize', onScroll);
        return () => {
            window.removeEventListener('scroll', onScroll, true);
            window.removeEventListener('resize', onScroll);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onDown = (event) => {
            if (wrapRef.current?.contains(event.target)) return;
            if (listRef.current?.contains(event.target)) return;
            close();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    const commit = (next) => {
        if (next === committedRef.current) return;
        committedRef.current = next;
        if (onCommit) onCommit(next);
    };

    const close = () => {
        setOpen(false);
        setHighlight(-1);
        setQuery(null);
    };

    const choose = (option) => {
        if (onValueChange) onValueChange(option);
        commit(option);
        close();
        inputRef.current?.focus();
    };

    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            if (!open) { setOpen(true); setHighlight(0); return; }
            const step = event.key === 'ArrowDown' ? 1 : -1;
            setHighlight((prev) => {
                const next = prev + step;
                if (next < 0) return filtered.length - 1;
                if (next >= filtered.length) return 0;
                return next;
            });
            return;
        }
        if (event.key === 'Enter') {
            if (open && highlight >= 0 && filtered[highlight] !== undefined) {
                event.preventDefault();
                choose(filtered[highlight]);
            } else {
                commit(value);
                close();
            }
            return;
        }
        if (event.key === 'Escape' && open) {
            event.preventDefault();
            close();
        }
    };

    return (
        <div className={`combo-input${open ? ' combo-input--open' : ''}`} ref={wrapRef}>
            <input
                ref={inputRef}
                type="text"
                role="combobox"
                aria-expanded={open}
                autoComplete="off"
                placeholder={placeholder}
                value={value || ''}
                onDoubleClick={onDoubleClick}
                onChange={(event) => {
                    if (onValueChange) onValueChange(event.target.value);
                    setQuery(event.target.value);
                    setHighlight(-1);
                    if (!open) setOpen(true);
                }}
                onFocus={() => place()}
                onBlur={(event) => commit(event.target.value)}
                onKeyDown={onKeyDown}
            />
            <button
                type="button"
                className="combo-input__arrow"
                tabIndex={-1}
                aria-label="Show options"
                // mousedown, not click: the input must not blur and re-close it first.
                onMouseDown={(event) => {
                    event.preventDefault();
                    if (open) { close(); } else { setQuery(null); setHighlight(-1); setOpen(true); }
                    inputRef.current?.focus();
                }}
            >
                <FontAwesomeIcon icon={faChevronDown} />
            </button>

            {open && box && createPortal(
                <div
                    ref={listRef}
                    className="combo-input__menu"
                    style={{
                        left: box.left,
                        width: box.width,
                        ...(box.top !== null ? { top: box.top } : { bottom: box.bottom }),
                        maxHeight: box.maxHeight,
                    }}
                >
                    {filtered.length ? filtered.map((option, index) => {
                        const selected = String(option) === String(value);
                        return (
                            <div
                                key={`${option}-${index}`}
                                className={`combo-input__option${index === highlight ? ' is-active' : ''}${selected ? ' is-selected' : ''}`}
                                // mousedown so the choice lands before the input blurs.
                                onMouseDown={(event) => { event.preventDefault(); choose(option); }}
                                onMouseEnter={() => setHighlight(index)}
                            >
                                <span>{option}</span>
                                {selected && <FontAwesomeIcon icon={faCheck} className="combo-input__tick" />}
                            </div>
                        );
                    }) : (
                        <div className="combo-input__empty">{emptyText}</div>
                    )}
                </div>,
                document.body
            )}
        </div>
    );
};

export default ComboInput;
